import { ReportType } from "@app-contexts/dashboard/shared/types/reports";
import Repository, {
  ExtendedAxiosRequestConfig,
} from "@shared/services/api/abstracts/Repository";
import { UrlRouteMap } from "@shared/services/api/types";
import * as RepositoryUrlUtils from "@shared/services/api/utils/RepositoryUrlUtils";
import {
  GetTaskFromJobIdReturnType,
  TaskedEndpointResponse,
} from "@shared/services/loaders/api";
import { taskLoader } from "@shared/services/loaders/task-loader";
import { AxiosRequestConfig } from "axios";
import type {
  BulkTicketNameChangePayload,
  BulkTicketNameChangeResponse,
} from "@app-contexts/dashboard/features/transactions/data/types/ticket-name-change";
import type {
  ExchangeTicketsAmountsType,
  VenueBasedInvoicePaginatedSerializer,
  VenueBasedInvoiceSerializer,
} from "../../types/invoice";

export default class VenueBasedInvoiceRepository extends Repository<VenueBasedInvoiceSerializer> {
  public readonly repositoryName = "VenueBasedInvoiceRepository";

  public readonly endpoint = RepositoryUrlUtils.venueBasedUrl(
    "financials/invoices/"
  );

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      items: "items",
      notify: "{transactionId?}/notify",
      getExchangeAmounts: "{transactionId}/get-exchange-amounts",
      getReceiptPdf: `{transactionId}/get-receipt-pdf`,
      payoutStats: `{transactionId}/payout-stats`,
      refund: `{transactionId}/refund`,
      itemizedExchange: "{transactionId}/itemized-exchange",
      stats: "stats",
      exportCsv: "export-csv",
      bulkTicketNameChange: "{transactionId}/bulk-ticket-name-change",
      void: "{transactionId}/void",
    };
  }

  public async items(
    config?: AxiosRequestConfig
  ): Promise<VenueBasedInvoicePaginatedSerializer> {
    const { data } = await this.request<VenueBasedInvoicePaginatedSerializer>(
      this.buildUrl("items"),
      config
    );

    return data;
  }

  public async notify(
    config?: AxiosRequestConfig
  ): Promise<VenueBasedInvoicePaginatedSerializer> {
    const { data } = await this.request<VenueBasedInvoicePaginatedSerializer>(
      this.buildUrl("notify"),
      {
        method: "post",
        ...config,
      }
    );

    return data;
  }

  public async getExchangeAmounts(
    config?: ExtendedAxiosRequestConfig
  ): Promise<ExchangeTicketsAmountsType> {
    const { data } = await this.request<ExchangeTicketsAmountsType>(
      this.buildUrl("getExchangeAmounts"),
      {
        ...config,
      }
    );

    return data;
  }

  public async getReceiptPdf(
    transactionId: string,
    config: AxiosRequestConfig,
    language?: "en" | "fr"
  ): Promise<GetTaskFromJobIdReturnType> {
    const params = language ? { ...config?.params, language } : config?.params;
    const { data } = await this.request<TaskedEndpointResponse>(
      this.buildUrl("getReceiptPdf", { transactionId }),
      { ...config, params }
    );

    return taskLoader(data.job_id);
  }

  public async payoutStats(
    transactionId: string,
    config: AxiosRequestConfig
  ): Promise<ReportType> {
    const { data } = await this.request<ReportType>(
      this.buildUrl("payoutStats", { transactionId }),
      config
    );
    return data;
  }

  public async refund(
    transactionId: string,
    config?: AxiosRequestConfig
  ): Promise<GetTaskFromJobIdReturnType> {
    const { data } = await this.request<{ job_id: string }>(
      this.buildUrl("refund", { transactionId }),
      {
        method: "post",
        ...config,
      }
    );

    return taskLoader(data.job_id);
  }

  public async bulkTicketNameChange(
    transactionId: string,
    payload: BulkTicketNameChangePayload,
    config?: AxiosRequestConfig
  ): Promise<BulkTicketNameChangeResponse> {
    const { data } = await this.request<BulkTicketNameChangeResponse>(
      this.buildUrl("bulkTicketNameChange", { transactionId }),
      { method: "patch", data: payload, ...config }
    );
    return data;
  }

  public async itemizedExchange(
    transactionId: string,
    config?: AxiosRequestConfig
  ): Promise<VenueBasedInvoiceSerializer> {
    const { data } = await this.request<VenueBasedInvoiceSerializer>(
      this.buildUrl("itemizedExchange", { transactionId }),
      {
        method: "post",
        ...config,
      }
    );

    return data;
  }

  public async exportCsv(
    config?: AxiosRequestConfig
  ): Promise<GetTaskFromJobIdReturnType<string>> {
    const { data } = await this.request<TaskedEndpointResponse>(
      this.buildUrl("exportCsv"),
      { ...config, method: "GET" }
    );

    return taskLoader(data.job_id);
  }
}
